import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  TextInput,
  ActionList,
  IconButton,
  Flash,
  Dialog,
  Text,
} from "@primer/react";
import {
  AlertIcon,
  FileBadgeIcon,
  SearchIcon,
  TrashIcon,
  PencilIcon,
  CalendarIcon,
} from "@primer/octicons-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { khoanNopGroupApi } from "../../../api/sf_khoannop_group_api";
import { saleSetRegisterDichVuApi } from "../../../api/sf_saleset_register_dichvu_api";
import { ComboboxNamHoc } from "../../../components/combobox-namhoc";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { NotifyHelper } from "../../../helpers/toast";
import { KhoanNopByHocSinhItemResponse } from "../../../models/response/dich-vu/KhoanNopByHocSinhItemResponse";

interface DangKyDichVuProps {
  hocSinh: any;
  canEdit?: boolean;
}

interface DateErrors {
  startDate?: string;
  endDate?: string;
}

const isNoiTruService = (serviceName: string): boolean => {
  return serviceName.toLowerCase().includes("nội trú");
};

interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  "aria-invalid"?: boolean;
  block?: boolean;
  className?: string;
  readOnly?: boolean;
}

const DangKyDichVu: React.FC<DangKyDichVuProps> = ({ hocSinh, canEdit = true }) => {
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const [namHocSelected, setNamHocSelected] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [backendError, setBackendError] = useState<string>("");
  const [khoanNopGroups, setKhoanNopGroups] = useState<
    KhoanNopByHocSinhItemResponse[]
  >([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [isShowDatePicker, setIsShowDatePicker] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    null
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateErrors, setDateErrors] = useState<DateErrors>({});
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
    ({ value, onClick, ...props }, ref) => (
      <TextInput
        ref={ref}
        value={value}
        onClick={onClick}
        {...props}
        readOnly
        leadingVisual={CalendarIcon}
      />
    )
  );

  const formatDateToDisplay = (date: Date | string | null): string => {
    if (!date) return "";
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  useEffect(() => {
    setNamHocSelected(nam_hoc);
  }, [nam_hoc]);

  useEffect(() => {
    if (namHocSelected && hocSinh.id) {
      loadKhoanNopGroups();
    }
  }, [namHocSelected, hocSinh.id]);

  const loadKhoanNopGroups = async () => {
    setIsLoading(true);
    setBackendError("");
    try {
      const res = await khoanNopGroupApi.SelectByHocSinh({
        ts_hocsinh_id: hocSinh.id,
        nam_hoc: namHocSelected,
        dm_coso_id,
      });

      if (res.is_success) {
        setKhoanNopGroups(res.data);
        setSelectedIds(
          res.data
            .filter((item: KhoanNopByHocSinhItemResponse) => item.is_checked)
            .map(
              (item: KhoanNopByHocSinhItemResponse) => item.sf_khoannop_group_id
            )
        );
      } else {
        setError(res.message || "Có lỗi khi tải dữ liệu");
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const extractErrorMessage = (error: any): string => {
    if (error.response) {
      if (typeof error.response.data === "string") {
        return error.response.data;
      } else if (error.response.data?.message) {
        return error.response.data.message;
      } else if (error.response.statusText) {
        return error.response.statusText;
      }
    }
    return error.message || "Có lỗi xảy ra";
  };

  const validateDates = (): boolean => {
    const errors: DateErrors = {};

    if (!startDate) {
      errors.startDate = "Vui lòng chọn ngày bắt đầu";
    }

    if (!endDate) {
      errors.endDate = "Vui lòng chọn ngày kết thúc";
    }

    if (startDate && endDate && startDate > endDate) {
      errors.endDate = "Ngày kết thúc phải sau ngày bắt đầu";
    }

    setDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditDates = (item: any) => {
    setSelectedItem(item);
    setSelectedServiceId(item.sf_khoannop_group_id);
    setIsEditMode(true);
    setIsShowDatePicker(true);
    setDateErrors({});

    if (item.tu_ngay) {
      const tuNgay = new Date(item.tu_ngay);
      tuNgay.setHours(12, 0, 0, 0);
      setStartDate(tuNgay);
    } else {
      setStartDate(new Date());
    }

    if (item.den_ngay) {
      const denNgay = new Date(item.den_ngay);
      denNgay.setHours(12, 0, 0, 0);
      setEndDate(denNgay);
    } else {
      setEndDate(null);
    }
  };

  const updateServiceRegistration = async (id: number, checked: boolean) => {
    setBackendError("");
    try {
      if (!checked) {
        const requestData = {
          id: 0,
          ts_hocsinh_id: hocSinh.id,
          nam_hoc: namHocSelected,
          sf_khoannop_group_id: 0,
          list_khoannop_group: [
            {
              sf_khoannop_group_id: id,
              is_checked: checked,
            },
          ],
        };

        const res = await saleSetRegisterDichVuApi.InsertMultiple(requestData);

        if (res.is_success) {
          setSelectedIds((prevIds) =>
            prevIds.filter((prevId) => prevId !== id)
          );
          setKhoanNopGroups((prevGroups) =>
            prevGroups.map((group) =>
              group.sf_khoannop_group_id === id
                ? { ...group, is_checked: checked }
                : group
            )
          );
          NotifyHelper.Success("Cập nhật thành công");
        } else {
          setBackendError(res.message || "Có lỗi khi cập nhật");
          NotifyHelper.Error(res.message || "Có lỗi khi cập nhật");
        }
        return;
      }

      if (!startDate || !endDate) {
        NotifyHelper.Error("Vui lòng chọn đầy đủ từ ngày và đến ngày");
        return;
      }

      const requestData = {
        id: 0,
        ts_hocsinh_id: hocSinh.id,
        nam_hoc: namHocSelected,
        sf_khoannop_group_id: 0,
        list_khoannop_group: [
          {
            sf_khoannop_group_id: id,
            is_checked: checked,
            tu_ngay: startDate,
            den_ngay: endDate,
          },
        ],
      };

      const res = await saleSetRegisterDichVuApi.InsertMultiple(requestData);

      if (res.is_success) {
        setSelectedIds((prevIds) =>
          checked ? [...prevIds, id] : prevIds.filter((prevId) => prevId !== id)
        );

        setKhoanNopGroups((prevGroups) =>
          prevGroups.map((group) =>
            group.sf_khoannop_group_id === id
              ? {
                ...group,
                is_checked: checked,
                tu_ngay: startDate,
                den_ngay: endDate,
              }
              : group
          )
        );
        NotifyHelper.Success("Cập nhật thành công");
      } else {
        setBackendError(res.message || "Có lỗi khi cập nhật");
        NotifyHelper.Error(res.message || "Có lỗi khi cập nhật");
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      setBackendError(errorMessage);
      NotifyHelper.Error(errorMessage);
    }
  };

  const updateDatesOnly = async (item: any) => {
    if (!startDate || !endDate) {
      NotifyHelper.Error("Vui lòng chọn đầy đủ từ ngày và đến ngày");
      return false;
    }

    try {
      const requestData = {
        id: item.id || 0,
        ts_hocsinh_id: hocSinh.id,
        nam_hoc: namHocSelected,
        sf_khoannop_group_id: item.sf_khoannop_group_id,
        tu_ngay: startDate,
        den_ngay: endDate,
      };

      const res = await saleSetRegisterDichVuApi.Update(requestData);

      if (res.is_success) {
        setKhoanNopGroups((prevGroups) =>
          prevGroups.map((group) =>
            group.sf_khoannop_group_id === item.sf_khoannop_group_id
              ? { ...group, tu_ngay: startDate, den_ngay: endDate }
              : group
          )
        );
        NotifyHelper.Success("Cập nhật thời gian thành công");
        return true;
      } else {
        setBackendError(res.message || "Có lỗi khi cập nhật");
        NotifyHelper.Error(res.message || "Có lỗi khi cập nhật");
        return false;
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      setBackendError(errorMessage);
      NotifyHelper.Error(errorMessage);
      return false;
    }
  };

  const handleRemoveItem = async (id: number) => {
    setItemToDelete(id);
    setIsShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete !== null) {
      setBackendError("");
      try {
        const requestData = {
          id: 0,
          ts_hocsinh_id: hocSinh.id,
          nam_hoc: namHocSelected,
          sf_khoannop_group_id: 0,
          list_khoannop_group: [
            {
              sf_khoannop_group_id: itemToDelete,
              is_checked: false,
            },
          ],
        };

        const res = await saleSetRegisterDichVuApi.InsertMultiple(requestData);

        if (res.is_success) {
          setSelectedIds((prevIds) =>
            prevIds.filter((id) => id !== itemToDelete)
          );
          setKhoanNopGroups((prevGroups) =>
            prevGroups.map((group) =>
              group.sf_khoannop_group_id === itemToDelete
                ? { ...group, is_checked: false }
                : group
            )
          );
          NotifyHelper.Success("Xóa thành công");
        } else {
          setBackendError(res.message || "Có lỗi khi xóa");
          NotifyHelper.Error(res.message || "Có lỗi khi xóa");
        }
      } catch (error: any) {
        const errorMessage = extractErrorMessage(error);
        setBackendError(errorMessage);
        NotifyHelper.Error(errorMessage);
      }
      setIsShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  const handleDateConfirm = async () => {
    if (!validateDates()) {
      return;
    }

    if (isEditMode && selectedItem) {
      const success = await updateDatesOnly(selectedItem);
      if (success) {
        setIsShowDatePicker(false);
        setSelectedServiceId(null);
        setSelectedItem(null);
      }
    } else if (selectedServiceId !== null) {
      await updateServiceRegistration(selectedServiceId, true);
      setIsShowDatePicker(false);
      setSelectedServiceId(null);
    }
  };

  const filteredKhoanNop = useMemo(() => {
    return khoanNopGroups.filter((item) =>
      item.ten_khoan_nop_group.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [khoanNopGroups, searchQuery]);

  const selectedItems = useMemo(() => {
    return khoanNopGroups.filter((item) =>
      selectedIds.includes(item.sf_khoannop_group_id)
    );
  }, [khoanNopGroups, selectedIds]);

  const ServiceItem: React.FC<{
    item: KhoanNopByHocSinhItemResponse;
    onRegister: (id: number, startDate: Date, endDate: Date) => Promise<void>;
    canEdit?: boolean;
  }> = ({ item, onRegister, canEdit = true }) => {
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [errors, setErrors] = useState<DateErrors>({});

    const handleRegister = async () => {
      const newErrors: DateErrors = {};
      if (!startDate) newErrors.startDate = "Vui lòng chọn ngày bắt đầu";
      if (!endDate) newErrors.endDate = "Vui lòng chọn ngày kết thúc";
      if (startDate && endDate && startDate > endDate) {
        newErrors.endDate = "Ngày kết thúc phải sau ngày bắt đầu";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      await onRegister(item.sf_khoannop_group_id, startDate, endDate);
    };

    return (
      <Box

      >
        {/* Tên dịch vụ */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid",
            borderBottomColor: "border.default"
          }}
        >

          <Box sx={{ flex: 1, display: "grid", gap: 2 }}>
            <Box>  <Text sx={{ fontWeight: "bold" }}>{item.ten_khoan_nop_group}</Text></Box>
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Text sx={{ fontSize: "12px", mb: 1, color: "fg.muted" }}>
                  Từ ngày
                </Text>
                <Box sx={{ width: "150px" }}>
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => {
                      if (date) {
                        setStartDate(date);
                        setErrors((prev) => ({ ...prev, startDate: undefined }));
                      }
                    }}
                    dateFormat="dd/MM/yyyy"
                    customInput={<CustomInput aria-invalid={!!errors.startDate} />}
                    disabled={!isNoiTruService(item.ten_khoan_nop_group)}
                  />
                </Box>
                {errors.startDate && (
                  <Text sx={{ color: "danger.emphasis", fontSize: "12px", mt: 1 }}>
                    {errors.startDate}
                  </Text>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Text sx={{ fontSize: "12px", mb: 1, color: "fg.muted" }}>
                  Đến ngày
                </Text>
                <Box sx={{ width: "150px" }}>
                  <DatePicker
                    selected={endDate}
                    onChange={(date: Date | null) => {
                      if (date) {
                        setEndDate(date);
                        setErrors((prev) => ({ ...prev, endDate: undefined }));
                      }
                    }}
                    dateFormat="dd/MM/yyyy"
                    customInput={<CustomInput aria-invalid={!!errors.endDate} />}
                    minDate={startDate || undefined}
                    disabled={!isNoiTruService(item.ten_khoan_nop_group)}
                  />
                </Box>
                {errors.endDate && (
                  <Text sx={{ color: "danger.emphasis", fontSize: "12px", mt: 1 }}>
                    {errors.endDate}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
          <Box>
            <Box sx={{ alignSelf: "flex-end", display: "flex" }}>
              <Button
                onClick={handleRegister}
                variant="primary"
                sx={{ width: "100%" }}
                disabled={!isNoiTruService(item.ten_khoan_nop_group)}
              >
                Đăng ký
              </Button>
            </Box>
          </Box>
        </Box>


      </Box>
    );
  };

  return (
    <Box sx={{ padding: 0 }}>
      <Box sx={{ display: "flex", gap: 3, marginBottom: 3 }}>
        <FormControl>
          <FormControl.Label>Năm học</FormControl.Label>
          <ComboboxNamHoc
            value={namHocSelected}
            onValueChanged={(value: string) => setNamHocSelected(value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <FormControl.Label>Tìm kiếm</FormControl.Label>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextInput
              sx={{ flex: 1 }}
              leadingVisual={SearchIcon}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nhập để tìm kiếm..."
            />
          </Box>
        </FormControl>
      </Box>

      {error && (
        <Flash variant="danger" sx={{ mb: 3 }}>
          {error}
        </Flash>
      )}

      {backendError && (
        <Flash variant="danger" sx={{ mb: 3 }}>
          <strong>Lỗi:</strong> {backendError}
        </Flash>
      )}

      <Dialog
        isOpen={isShowDeleteConfirm}
        onDismiss={() => setIsShowDeleteConfirm(false)}
      >
        <Dialog.Header>
          <Box display="flex" alignItems="center">
            <div style={{ color: "orange" }}>
              <AlertIcon size={28} />
            </div>
            <Box ml={2}>Lưu ý</Box>
          </Box>
        </Dialog.Header>
        <Box p={3}>
          <Text>Bạn có chắc chắn muốn xóa dịch vụ này?</Text>
          <Box display="flex" mt={3} justifyContent="flex-end">
            <Box mr={2}>
              <Button
                variant="default"
                onClick={() => setIsShowDeleteConfirm(false)}
              >
                Đóng
              </Button>
            </Box>
            <Button variant="danger" onClick={confirmDelete}>
              Xóa
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        isOpen={isShowDatePicker}
        onDismiss={() => setIsShowDatePicker(false)}
      >
        <Dialog.Header>
          {isEditMode ? "Cập nhật thời gian" : "Chọn ngày"}{" "}
          <span className="text-red-500">*</span>
        </Dialog.Header>
        <Box p={3}>
          <FormControl>
            <FormControl.Label>
              Ngày bắt đầu <span className="text-red-500">*</span>
            </FormControl.Label>
            <DatePicker
              selected={startDate}
              onChange={(date: Date | null) => {
                if (date) {
                  setStartDate(date);
                  setDateErrors((prev) => ({ ...prev, startDate: undefined }));
                }
              }}
              dateFormat="dd/MM/yyyy"
              customInput={
                <CustomInput
                  block
                  className={
                    dateErrors.startDate ? "border border-red-500" : ""
                  }
                  aria-invalid={!!dateErrors.startDate}
                />
              }
            />
            {dateErrors.startDate && (
              <div className="text-red-500 text-sm mt-1">
                {dateErrors.startDate}
              </div>
            )}
          </FormControl>

          <FormControl>
            <FormControl.Label>
              Ngày kết thúc <span className="text-red-500">*</span>
            </FormControl.Label>
            <DatePicker
              selected={endDate}
              onChange={(date: Date | null) => {
                if (date) {
                  setEndDate(date);
                  setDateErrors((prev) => ({ ...prev, endDate: undefined }));
                }
              }}
              dateFormat="dd/MM/yyyy"
              customInput={
                <CustomInput
                  block
                  className={dateErrors.endDate ? "border border-red-500" : ""}
                  aria-invalid={!!dateErrors.endDate}
                />
              }
              minDate={startDate || undefined}
            />
            {dateErrors.endDate && (
              <div className="text-red-500 text-sm mt-1">
                {dateErrors.endDate}
              </div>
            )}
          </FormControl>

          <Box display="flex" mt={3} justifyContent="flex-end">
            <Box mr={2}>
              <Button
                variant="default"
                onClick={() => setIsShowDatePicker(false)}
              >
                Đóng
              </Button>
            </Box>
            <Button variant="primary" onClick={handleDateConfirm}>
              {isEditMode ? "Cập nhật" : "Xác nhận"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              maxHeight: "calc(100vh - 200px)",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                bg: "canvas.subtle",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "border.default",
                fontWeight: "bold",
                color: "fg.muted",
              }}
            >
              Danh sách dịch vụ
            </Box>

            {filteredKhoanNop
              .filter(
                (item) => !selectedIds.includes(item.sf_khoannop_group_id)
              )
              .map((item) => (
                <ServiceItem
                  key={item.sf_khoannop_group_id}
                  item={item}
                  canEdit={canEdit}
                  onRegister={async (
                    id: number,
                    startDate: Date,
                    endDate: Date
                  ) => {
                    // Xử lý đăng ký ở component cha
                    const requestData = {
                      id: 0,
                      ts_hocsinh_id: hocSinh.id,
                      nam_hoc: namHocSelected,
                      sf_khoannop_group_id: 0,
                      list_khoannop_group: [
                        {
                          sf_khoannop_group_id: id,
                          is_checked: true,
                          tu_ngay: startDate,
                          den_ngay: endDate,
                        },
                      ],
                    };

                    try {
                      const res = await saleSetRegisterDichVuApi.InsertMultiple(
                        requestData
                      );
                      if (res.is_success) {
                        setSelectedIds((prevIds) => [...prevIds, id]);
                        setKhoanNopGroups((prevGroups) =>
                          prevGroups.map((group) =>
                            group.sf_khoannop_group_id === id
                              ? {
                                ...group,
                                is_checked: true,
                                tu_ngay: startDate,
                                den_ngay: endDate,
                              }
                              : group
                          )
                        );
                        NotifyHelper.Success("Đăng ký thành công");
                        loadKhoanNopGroups();
                      } else {
                        NotifyHelper.Error(res.message || "Có lỗi khi đăng ký");
                      }
                    } catch (error) {
                      const errorMessage = extractErrorMessage(error);
                      NotifyHelper.Error(errorMessage);
                    }
                  }}
                />
              ))}

            {filteredKhoanNop.filter(
              (item) => !selectedIds.includes(item.sf_khoannop_group_id)
            ).length === 0 && (
                <Box sx={{ p: 4, textAlign: "center", color: "fg.muted" }}>
                  Không có dịch vụ nào chưa đăng ký
                </Box>
              )}
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              maxHeight: "calc(100vh - 420px)",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                bg: "canvas.subtle",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "border.default",
                fontWeight: "bold",
                color: "fg.muted",
              }}
            >
              Danh sách đã chọn
            </Box>
            <ActionList showDividers>
              {selectedItems.map((item) => (
                <ActionList.Item key={item.sf_khoannop_group_id} style={{
                  padding: "8px"
                }}>

                  <Box sx={{ display: "flex", flex: 1, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: "100%",
                        pr: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.ten_khoan_nop_group}
                      {item.tu_ngay && item.den_ngay && (
                        <Box
                          as="span"
                          sx={{
                            color: "fg.muted",
                            fontSize: "12px",
                            display: "block",
                          }}
                        >
                          {formatDateToDisplay(item.tu_ngay)} -{" "}
                          {formatDateToDisplay(item.den_ngay)}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {canEdit && (
                    <ActionList.TrailingVisual>
                      <Box sx={{ display: "flex", gap: 1, m: -1 }}>
                        <IconButton
                          aria-label="Chỉnh sửa thời gian"
                          size="small"
                          icon={PencilIcon}
                          variant="invisible"
                          onClick={() => handleEditDates(item)}
                          disabled={!isNoiTruService(item.ten_khoan_nop_group)}
                        />
                        <IconButton
                          aria-label="Xóa"
                          size="small"
                          icon={TrashIcon}
                          variant="danger"
                          onClick={() =>
                            handleRemoveItem(item.sf_khoannop_group_id)
                          }
                          disabled={!isNoiTruService(item.ten_khoan_nop_group)}
                        />
                      </Box>
                    </ActionList.TrailingVisual>
                  )}
                </ActionList.Item>
              ))}
              {selectedItems.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center", color: "fg.muted" }}>
                  Chưa có dịch vụ nào được chọn
                </Box>
              )}
            </ActionList>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DangKyDichVu;
